import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Loader2, Copy, Eye } from "lucide-react";
import { toast } from "sonner";

interface OfferRow {
  id: string;
  slug: string;
  access_code: string;
  client_name: string;
  client_company: string | null;
  title: string;
  status: "draft" | "active" | "archived";
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
}

const AdminOffers = () => {
  const [offers, setOffers] = useState<OfferRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("offers")
      .select("id, slug, access_code, client_name, client_company, title, status, view_count, last_viewed_at, created_at")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setOffers((data as OfferRow[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const copyLink = (o: OfferRow) => {
    const url = `${window.location.origin}/offre/${o.slug}?code=${encodeURIComponent(o.access_code)}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié (avec code pré-rempli)");
  };

  const copyCode = (o: OfferRow) => {
    navigator.clipboard.writeText(o.access_code);
    toast.success("Code copié");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Offres</h1>
          <p className="text-muted-foreground">Propositions commerciales personnalisées.</p>
        </div>
        <Button asChild>
          <Link to="/admin/offres/nouveau">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle offre
          </Link>
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        </div>
      ) : offers.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Aucune offre. Crées-en une pour commencer.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {offers.map((o) => (
            <Card key={o.id} className="p-4 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="font-semibold truncate">{o.client_name}</h3>
                  {o.client_company && (
                    <span className="text-xs text-muted-foreground">· {o.client_company}</span>
                  )}
                  <Badge
                    variant={
                      o.status === "active" ? "default" : o.status === "draft" ? "secondary" : "outline"
                    }
                  >
                    {o.status === "active" ? "Active" : o.status === "draft" ? "Brouillon" : "Archivée"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground truncate">{o.title}</p>
                <p className="text-xs text-muted-foreground mt-1 font-mono">
                  /offre/{o.slug} · code: {o.access_code} · {o.view_count} vue{o.view_count !== 1 ? "s" : ""}
                  {o.last_viewed_at && ` · dernière ${new Date(o.last_viewed_at).toLocaleDateString("fr-CA")}`}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => copyCode(o)}>
                  <Copy className="h-3 w-3 mr-1" /> Code
                </Button>
                <Button size="sm" variant="outline" onClick={() => copyLink(o)}>
                  <Copy className="h-3 w-3 mr-1" /> Lien
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a
                    href={`/offre/${o.slug}?code=${encodeURIComponent(o.access_code)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Eye className="h-3 w-3" />
                  </a>
                </Button>
                <Button size="sm" asChild>
                  <Link to={`/admin/offres/${o.slug}`}>Éditer</Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOffers;
